<template>
  <div class="dm-ledger-rule fts-server-native-row">
    <div class="dm-ledger-rule-label" :title="tooltip">
      <span class="server-native-dot" :class="dotClass"></span>
      Server-native formulas
      <small class="server-native-hint" v-if="hint">{{ hint }}</small>
    </div>
    <div class="dm-ledger-states" :class="{ 'server-native-states--locked': toggleDisabled }">
      <span
        class="dm-ledger-state"
        :class="{ 'dm-ledger-state--on': !enabled }"
        @click="enabled && setEnabled(false)"
      >Off</span>
      <span class="dm-ledger-sep">·</span>
      <span
        class="dm-ledger-state"
        :class="{ 'dm-ledger-state--on': enabled }"
        @click="!enabled && setEnabled(true)"
      >On</span>
    </div>
  </div>
</template>

<script>
import { authorizationStore } from "../store/authorizationStore";

/* The per-activity server-native formula opt-in as an Advanced ledger row (moved out of the
   form designer's footer, ruling 2026-08-10). Same semantics as the old toggle: serverNative
   on the ACTIVITY node, master-plan gating (2026-08-01) — on is the default, only internal /
   Consultant identities may relax to Python, and relaxing warns first. The verdict classifies
   the SAVED canvas, so fixing formulas re-enables after a save. */
export default {
  props: {
    // the canvas ACTIVITY node (carries serverNative)
    activity: Object,
    activityId: String
  },
  data: () => ({
    verdict: null
  }),
  computed: {
    moduleId() {
      return this.$store.state.vectorId.toString();
    },
    enabled() {
      return Boolean(this.activity) && this.activity.serverNative === true;
    },
    canRelax() {
      const roles = authorizationStore.getters.userRoles || [];
      return authorizationStore.getters.isInternal || roles.indexOf("Consultant") > -1;
    },
    toggleDisabled() {
      if (this.enabled && !this.canRelax) return true;
      return !this.enabled
        && Boolean(this.verdict)
        && (!this.verdict.compatible || !this.verdict.hasFormulas);
    },
    dotClass() {
      if (!this.enabled) return "server-native-dot--manual";
      if (this.verdict && !this.verdict.hasFormulas) return "server-native-dot--manual";
      return this.verdict && this.verdict.compatible
        ? "server-native-dot--native"
        : "server-native-dot--python";
    },
    hint() {
      if (!this.enabled) return "Python engine";
      if (!this.verdict) return null;
      if (!this.verdict.hasFormulas) return "no formulas";
      return this.verdict.compatible ? "all formulas compatible" : "runs on Python";
    },
    tooltip() {
      const verdict = this.verdict;
      const offenders = verdict && verdict.nonNativeFields && verdict.nonNativeFields.length
        ? ` Fields needing Python: ${verdict.nonNativeFields.join(", ")}.`
        : "";

      if (!this.enabled) {
        if (verdict && verdict.hasFormulas && verdict.compatible)
          return "This form's formulas are compatible with the server-native engine — turn it on to use it";
        if (verdict && verdict.hasFormulas && !verdict.compatible)
          return `Not compatible — these formulas need the Python engine (re-checked on save).${offenders}`;
        if (verdict && !verdict.hasFormulas)
          return "This form has no formulas to optimize (re-checked on save)";
        return "Server-native formulas are off";
      }
      if (!this.canRelax)
        return "Server-native is the platform default and stays on. Contact support if this design misbehaves on the native engine.";
      if (verdict && !verdict.hasFormulas)
        return "This form has no formulas to optimize";
      if (verdict && verdict.compatible)
        return "This form's formulas run on the server-native engine (as of the last save)";
      if (verdict && verdict.hasBackendScript)
        return `A custom backend script forces the Python engine.${offenders}`;
      return verdict
        ? `Runs on the Python engine.${offenders}`
        : "Verdict pending — save the design to classify";
    }
  },
  methods: {
    async setEnabled(val) {
      if (!this.activity || this.toggleDisabled) return;
      if (val) {
        this.$set(this.activity, "serverNative", true);
        return;
      }
      // Master plan (2026-08-01): internal/consultant users may relax a design to the Python
      // engine after an explicit warning; normal users never reach here (states locked).
      if (!this.canRelax) return;
      const res = await this.$confirmation.show({
        title: "Switch to the Python engine?",
        text: "Server-native is the platform default and the faster engine. Move this form's formulas back to Python only for a confirmed incompatibility or result divergence."
      });
      if (res === true && this.activity) {
        this.$delete(this.activity, "serverNative");
      }
    },
    // advisory verdict for the dot — classifies the SAVED canvas (the endpoint's contract);
    // failures never surface, the dot just stays pending
    async refreshVerdict() {
      if (!this.activity || !this.activityId) return;
      try {
        const result = await this.$store.dispatch("classifyFormulaNative", {
          vectorId: this.moduleId,
          activityId: this.activityId
        });
        this.verdict = result.data;
      } catch (err) {
        this.verdict = null;
      }
      // the standard-activity designer shares this verdict: its "Run automation & script on
      // view" row locks "Off" while viewVisibilityNative is false (ruling 2026-08-23)
      this.$emit("verdict", this.verdict);
    }
  },
  mounted() {
    this.refreshVerdict();
  }
};
</script>

<style lang="scss">
.fts-server-native-row {
  .server-native-dot {
    flex: 0 0 auto;
    width: 0.4rem;
    height: 0.4rem;
    margin-right: 0.375rem;
    border-radius: 50%;
    align-self: center;
    background-color: var(--fts-border);
    &.server-native-dot--native { background-color: #2e7d32; }
    &.server-native-dot--python { background-color: var(--fts-formula-ref, #b26a00); }
  }
  .dm-ledger-rule-label {
    display: flex;
    align-items: baseline;
  }
  .server-native-hint {
    margin-left: 0.5rem;
    font-size: 0.625rem;
    color: var(--fts-form-helper);
  }
  .server-native-states--locked {
    opacity: 0.5;
    pointer-events: none;
  }
}
</style>
